// src/components/nexilink/status-badge.tsx
// Domain-specific badges built on shadcn/ui Badge
import React from 'react';
import { Check } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

// Match quality badge for candidate matching
interface MatchQualityBadgeProps {
    quality: 'good' | 'fair' | 'poor';
    score?: number;
    showScore?: boolean;
    className?: string;
}

export function MatchQualityBadge({
    quality,
    score,
    showScore = false,
    className
}: MatchQualityBadgeProps) {
    const qualityConfig = {
        good: {
            variant: 'default' as const,
            classes: 'bg-green-100 text-green-700 border-green-200 hover:bg-green-100',
            dot: 'bg-green-500',
            label: 'Matchar'
        },
        fair: {
            variant: 'secondary' as const,
            classes: 'bg-yellow-100 text-yellow-700 border-yellow-200 hover:bg-yellow-100',
            dot: 'bg-yellow-500',
            label: 'Matchar delvis'
        },
        poor: {
            variant: 'destructive' as const,
            classes: 'bg-red-100 text-red-700 border-red-200 hover:bg-red-100',
            dot: 'bg-red-500',
            label: 'Matchar ej'
        }
    };

    const config = qualityConfig[quality];

    return (
        <div className={cn('flex items-center gap-2', className)}>
            <Badge className={cn('gap-1.5', config.classes)}>
                <span className={cn('w-1.5 h-1.5 rounded-full', config.dot)} />
                {config.label}
            </Badge>
            {showScore && score !== undefined && (
                <Badge className="bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-100">
                    {score}% match
                </Badge>
            )}
        </div>
    );
}

// Status badge for needs/applications
interface StatusBadgeProps {
    status: string;
    label?: string;
    dot?: boolean;
    className?: string;
}

export function StatusBadge({
    status,
    label,
    dot = true,
    className
}: StatusBadgeProps) {
    const statusConfig: Record<string, { classes: string; dot: string; label: string; icon?: React.ComponentType<{ className?: string }> }> = {
        active: {
            classes: 'bg-green-100 text-green-700 border-green-200 hover:bg-green-100',
            dot: 'bg-green-500',
            label: 'Aktiv'
        },
        draft: {
            classes: 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-100',
            dot: 'bg-gray-500',
            label: 'Utkast'
        },
        // #75 course lifecycle. published = live/usable (green, like an active
        // need); archived = retired (muted stone, distinct from a never-live
        // draft's gray). Candidate progress statuses reuse the keys above/below.
        published: {
            classes: 'bg-green-100 text-green-700 border-green-200 hover:bg-green-100',
            dot: 'bg-green-500',
            label: 'Publicerad'
        },
        archived: {
            classes: 'bg-stone-100 text-stone-600 border-stone-200 hover:bg-stone-100',
            dot: 'bg-stone-400',
            label: 'Arkiverad'
        },
        filled: {
            classes: 'bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-100',
            dot: 'bg-blue-500',
            label: 'Tillsatt'
        },
        cancelled: {
            classes: 'bg-red-100 text-red-700 border-red-200 hover:bg-red-100',
            dot: 'bg-red-500',
            label: 'Avbruten'
        },
        paused: {
            classes: 'bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-100',
            dot: 'bg-amber-500',
            label: 'Pausad'
        },
        new: {
            classes: 'bg-teal-100 text-teal-700 border-teal-200 hover:bg-teal-100',
            dot: 'bg-teal-500',
            label: 'Ny'
        },
        reviewed: {
            classes: 'bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-100',
            dot: 'bg-blue-500',
            label: 'Granskad'
        },
        contacted: {
            classes: 'bg-indigo-100 text-indigo-700 border-indigo-200 hover:bg-indigo-100',
            dot: 'bg-indigo-500',
            label: 'Kontaktad'
        },
        hired: {
            classes: 'bg-green-100 text-green-700 border-green-200 hover:bg-green-100',
            dot: 'bg-green-500',
            label: 'Anställd'
        },
        rejected: {
            classes: 'bg-red-100 text-red-700 border-red-200 hover:bg-red-100',
            dot: 'bg-red-500',
            label: 'Nekad'
        },
        // Job application statuses
        pending: {
            classes: 'bg-yellow-100 text-yellow-700 border-yellow-200 hover:bg-yellow-100',
            dot: 'bg-yellow-500',
            label: 'Inskickad'
        },
        reviewing: {
            classes: 'bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-100',
            dot: 'bg-blue-500',
            label: 'Under granskning'
        },
        accepted: {
            classes: 'bg-green-100 text-green-700 border-green-200 hover:bg-green-100',
            dot: 'bg-green-500',
            label: 'Accepterad'
        },
        withdrawn: {
            classes: 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-100',
            dot: 'bg-gray-500',
            label: 'Återkallad'
        },
        closed: {
            classes: 'bg-red-100 text-red-700 border-red-200 hover:bg-red-100',
            dot: 'bg-red-500',
            label: 'Stängd'
        },
        // #281 — a need whose application deadline auto-passed; awaiting review.
        // Orange = "needs attention" (distinct from amber paused / red closed).
        expired: {
            classes: 'bg-orange-100 text-orange-700 border-orange-200 hover:bg-orange-100',
            dot: 'bg-orange-500',
            label: 'Utgången'
        },
        // Onboarding / course progress statuses — unified app-wide so every
        // "Slutförd / Pågår / Ej påbörjad" badge looks and reads the same.
        not_started: {
            classes: 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-100',
            dot: 'bg-gray-400',
            label: 'Ej påbörjad'
        },
        in_progress: {
            classes: 'bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-100',
            dot: 'bg-blue-500',
            label: 'Pågår'
        },
        completed: {
            // A finished course is "done, won't be used again" — mark it with a
            // check instead of a status dot, distinct from an ongoing/published dot.
            classes: 'bg-green-100 text-green-700 border-green-200 hover:bg-green-100',
            dot: 'bg-green-500',
            label: 'Slutförd',
            icon: Check
        },
    };

    const config = statusConfig[status] || {
        classes: 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-100',
        dot: 'bg-gray-500',
        label: status
    };

    const Icon = config.icon;
    return (
        <Badge className={cn('gap-1.5', config.classes, className)}>
            {Icon
                ? <Icon className="w-3 h-3" />
                : dot && <span className={cn('w-1.5 h-1.5 rounded-full', config.dot)} />}
            {label || config.label}
        </Badge>
    );
}
